import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const ViewProductPrintImagesCanvasGrid = ({
  src,
  showGrid,
  showOriginalCanvasSize,
  showBackgroundFill,
  dpi = 300,
  originalCanvasWidth,
  originalCanvasHeight
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [printSize, setPrintSize] = useState({
    widthInches: 0,
    heightInches: 0
  });

  const drawGrid = () => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (
      !container ||
      !canvas ||
      (!showGrid && !showOriginalCanvasSize && !showBackgroundFill)
    ) {
      return;
    }

    const ctx = canvas.getContext('2d');
    const { clientWidth, clientHeight } = container;

    canvas.width = clientWidth;
    canvas.height = clientHeight;

    const { widthInches, heightInches } = printSize;
    const scale = Math.min(
      clientWidth / (widthInches * dpi),
      clientHeight / (heightInches * dpi)
    );

    const imgWidthPixels = widthInches * dpi * scale;
    const imgHeightPixels = heightInches * dpi * scale;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (showOriginalCanvasSize) {
      // ===== BLUE DESIGN CANVAS CENTERED HORIZONTALLY =====
      const designWidth = originalCanvasWidth * scale;
      const designHeight = originalCanvasHeight * scale;
      const designOffsetX = (imgWidthPixels - designWidth) / 2;
      const designOffsetY = 0; // top-aligned

      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'blue';
      ctx.rect(designOffsetX, designOffsetY, designWidth, designHeight);
      ctx.stroke();
    }

    if (showGrid) {
      // ===== RED GRID LINES =====
      ctx.strokeStyle = 'red';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);

      // vertical lines
      for (let i = 1; i < widthInches; i++) {
        const x = i * dpi * scale;
        if (x >= imgWidthPixels) break;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, imgHeightPixels);
        ctx.stroke();
      }

      // horizontal lines
      for (let i = 1; i < heightInches; i++) {
        const y = i * dpi * scale;
        if (y >= imgHeightPixels) break;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(imgWidthPixels, y);
        ctx.stroke();
      }
    }
  };

  const handleImageLoad = (e) => {
    const img = e.target;
    setPrintSize({
      widthInches: img.naturalWidth / dpi,
      heightInches: img.naturalHeight / dpi
    });
  };

  useEffect(() => {
    drawGrid();
    window.addEventListener('resize', drawGrid);
    return () => window.removeEventListener('resize', drawGrid);
  }, [printSize, showGrid, showOriginalCanvasSize, showBackgroundFill]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: 'auto',
        minHeight: 200,
        backgroundColor: showBackgroundFill ? '#000' : '#FFF'
      }}
    >
      <img
        alt="Print"
        onLoad={handleImageLoad}
        src={src}
        style={{ display: 'block', width: '100%', height: 'auto' }}
      />
      {(showGrid || showOriginalCanvasSize) && (
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none',
            width: '100%',
            height: '100%'
          }}
        />
      )}
    </div>
  );
};

ViewProductPrintImagesCanvasGrid.propTypes = {
  dpi: PropTypes.number,
  originalCanvasHeight: PropTypes.number,
  originalCanvasWidth: PropTypes.number,
  printHeightInches: PropTypes.number,
  printWidthInches: PropTypes.number,
  showBackgroundFill: PropTypes.bool,
  showGrid: PropTypes.bool,
  showOriginalCanvasSize: PropTypes.bool,
  src: PropTypes.string
};

export default ViewProductPrintImagesCanvasGrid;
